import { useCallback, useRef, useState, type ReactNode } from 'react';
import { T, Var, useGT } from 'gt-react';

const DATE_SHORT_FORMAT: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
};
const TIME_FORMAT: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
};
import {
  PaperclipIcon,
  CaretDownIcon,
  CaretUpIcon,
  ArrowsInIcon,
  ArrowsOutIcon,
} from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import { useEmailThreadViewer } from './hooks/useEmailThreadViewer';
import { EmailThreadEmail } from '@erp/rpc';
import {
  renderEmailAttachments,
  renderEmailBody,
  buildCidMap,
  SplitDocumentsSection,
  documentAssociationsToAttachmentItems,
} from './email-thread-utils';
import { isPdfOrExcelAttachment } from './utils';
import { EmailExportButton } from './EmailExportButton';

export interface EmailThreadViewerProps {
  conversationId: string | null;
  fetchOCR?: boolean;
  fetchExtractedPurchaseOrder?: boolean;
  onEmailSelect?: (email: EmailThreadEmail) => void;
  /** Document IDs to highlight (for showing source documents of invoices/POs) */
  highlightedDocumentIds?: string[];
  /** Email IDs to highlight */
  highlightedEmailIds?: string[];
  /** Source document ID (for invoices created from documents) */
  sourceDocumentId?: string;
  /** Source email ID (for invoices created from inbound email) */
  sourceEmailId?: string;
  /** When true, only PDF / Excel attachments are listed (hides inline images
   *  like signature logos). Opt-in — defaults to showing everything. */
  documentAttachmentsOnly?: boolean;
  /** Extra actions rendered inline in the thread header's button row (e.g. a
   *  Reply button), so callers don't need their own separate toolbar. */
  headerActions?: ReactNode;
  /** Show the admin-only "Export email" debug button. Defaults to true; set
   *  false where the export tool isn't relevant (e.g. the OMS v3 review page). */
  showExportButton?: boolean;
  /** Render the email cards plain — white background + a single neutral border,
   *  with no primary highlight — instead of the muted/highlighted default. Used
   *  by the OMS v3 review page where a highlighted source email isn't wanted. */
  plainCards?: boolean;
  /** Order emails newest-first instead of the default chronological (oldest-first). */
  newestFirst?: boolean;
}

export function EmailThreadViewer({
  conversationId,
  fetchOCR: _fetchOCR,
  fetchExtractedPurchaseOrder: _fetchExtractedPurchaseOrder,
  onEmailSelect,
  highlightedDocumentIds = [],
  highlightedEmailIds = [],
  sourceDocumentId,
  sourceEmailId,
  documentAttachmentsOnly = false,
  headerActions,
  showExportButton = true,
  plainCards = false,
  newestFirst = false,
}: EmailThreadViewerProps) {
  // Card chrome: plain (white + 1px neutral border) when opted in; otherwise the
  // muted card with a primary border on the highlighted source email.
  const emailCardClass = (isHighlighted: boolean): string => {
    if (plainCards) return 'bg-background rounded-md p-3 border border-border';
    return `bg-muted/30 rounded-md p-3 border ${
      isHighlighted
        ? 'border-primary border-2 bg-primary/5'
        : 'border-border/50'
    }`;
  };
  const [showAllAttachments, setShowAllAttachments] =
    useState(!sourceDocumentId);
  const prevSourceDocumentIdRef = useRef(sourceDocumentId);
  let showAll = showAllAttachments;

  if (prevSourceDocumentIdRef.current !== sourceDocumentId) {
    prevSourceDocumentIdRef.current = sourceDocumentId;
    showAll = !sourceDocumentId;
    if (showAllAttachments !== showAll) {
      setShowAllAttachments(showAll);
    }
  }
  const gt = useGT();
  const {
    threadData,
    isLoading,
    isFetching,
    error,
    threadMetadata,
    expandedEmails,
    splitDocsExpandedEmails,
    toggleEmailExpanded,
    expandAllEmails,
    collapseAllEmails,
    toggleSplitDocuments,
    refetch,
  } = useEmailThreadViewer({
    conversationId,
    onEmailSelect,
    // The source document of an invoice/PO may be a split child, which the
    // thread query excludes by default — fetch splits so we can show it.
    forceIncludeSplitDocuments: !!sourceDocumentId,
  });

  const handleRefreshData = useCallback(() => {
    refetch();
  }, [refetch]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-16 h-48">
        <p className="text-sm text-muted-foreground">Loading email thread...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-16 h-48">
        <p className="text-sm text-destructive">
          <T>
            Error loading email thread: <Var>{error.message}</Var>
          </T>
        </p>
      </div>
    );
  }

  if (!threadData?.emails?.length) {
    return (
      <div className="flex items-center justify-center p-16 h-48">
        <p className="text-sm text-muted-foreground">
          <T>No emails found in this thread</T>
        </p>
      </div>
    );
  }

  // Best-effort id for the admin "Export email" button: prefer the explicit
  // source email when set (typical AP invoice / AR payment / sales-order
  // case); otherwise fall back to the first email in the thread, which is
  // almost always the inbound trigger. The button itself is hidden for
  // non-admins, and the backend will surface a 404 if the id ends up being
  // an outbound email.
  const exportEmailId = sourceEmailId ?? threadData.emails[0]?.id ?? null;

  // Single expand/collapse toggle: if any email is open the button collapses
  // them all (and vice versa). "Any" (not "all") so the default — which opens
  // the first email — starts as "Collapse All".
  const anyEmailsExpanded = threadData.emails.some((e) =>
    expandedEmails.has(e.id),
  );

  const renderThreadHeader = () => (
    <div className="flex items-center gap-4">
      <span className="text-sm font-medium">
        <T context="email thread viewer header">Email Thread</T>
      </span>
      <div className="ml-auto flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={anyEmailsExpanded ? collapseAllEmails : expandAllEmails}
        >
          {anyEmailsExpanded ? (
            <>
              <ArrowsInIcon className="size-4" />
              <T context="button label to collapse all emails">Collapse All</T>
            </>
          ) : (
            <>
              <ArrowsOutIcon className="size-4" />
              <T context="button label to expand all emails">Expand All</T>
            </>
          )}
        </Button>
        {showExportButton && (
          <EmailExportButton inboundEmailId={exportEmailId} />
        )}
        {headerActions}
      </div>
    </div>
  );

  // Default order is chronological (oldest-first); `newestFirst` flips it by
  // createdAt without mutating the source array.
  const orderedEmails = newestFirst
    ? [...threadData.emails].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
    : threadData.emails;

  const renderThreadEmails = () => (
    <div className="space-y-1">
      {orderedEmails.map((email, index) => {
        const isExpanded = expandedEmails.has(email.id);
        // const isLatest = index === threadData.emails.length - 1;
        const isHighlighted = highlightedEmailIds.includes(email.id);
        const isSourceEmail = !!sourceEmailId && email.id === sourceEmailId;
        // The source document may be a split child (excluded from
        // documentAssociations); surface it in the main list so the exact
        // invoice/PO document is always shown.
        const sourceSplitAssociations = sourceDocumentId
          ? (email.splitDocumentAssociations ?? []).filter(
              (association) => association.document.id === sourceDocumentId,
            )
          : [];
        const displayedDocumentAssociations = [
          ...(email.documentAssociations ?? []),
          ...sourceSplitAssociations,
        ];
        const sourceDocumentMatch =
          isSourceEmail &&
          !!sourceDocumentId &&
          displayedDocumentAssociations.some(
            (association) => association.document.id === sourceDocumentId,
          );
        const sourceFilteredDocumentAssociations =
          !showAll && sourceDocumentMatch
            ? displayedDocumentAssociations.filter(
                (association) => association.document.id === sourceDocumentId,
              )
            : displayedDocumentAssociations;
        const sourceFilteredAttachments =
          !showAll && sourceDocumentMatch ? [] : email.attachments;
        // Opt-in: keep only document attachments (PDF / Excel), dropping inline
        // images such as email-signature logos. Applied before the count so the
        // "{n} attachments" label stays consistent with what's shown.
        const filteredDocumentAssociations = documentAttachmentsOnly
          ? sourceFilteredDocumentAssociations.filter((association) =>
              isPdfOrExcelAttachment({
                fileName:
                  association.document.originalFileName ||
                  association.document.name,
                mimeType: association.document.mimeType || undefined,
              }),
            )
          : sourceFilteredDocumentAssociations;
        const filteredAttachments = documentAttachmentsOnly
          ? (sourceFilteredAttachments ?? []).filter((attachment) =>
              isPdfOrExcelAttachment({
                fileName: attachment.fileName,
                mimeType: attachment.mimeType,
              }),
            )
          : sourceFilteredAttachments;
        const filteredCount =
          (filteredAttachments?.length ?? 0) +
          (filteredDocumentAssociations?.length ?? 0);
        const totalCount = email.attachmentCount ?? filteredCount;
        // When the source document is a split child it isn't one of the
        // email's original attachments, so "{filtered} of {total}" would be
        // misleading — label it as a split document instead.
        const sourceIsSplitDocument = sourceSplitAssociations.length > 0;
        const attachmentsLabel = showAll
          ? totalCount === 1
            ? gt('{count} attachment', {
                context: 'attachment count label (singular)',
                count: String(totalCount),
              })
            : gt('{count} attachments', {
                context: 'attachment count label (plural)',
                count: String(totalCount),
              })
          : sourceIsSplitDocument
            ? filteredCount === 1
              ? gt('{count} split document', {
                  context: 'split document count label (singular)',
                  count: String(filteredCount),
                })
              : gt('{count} split documents', {
                  context: 'split document count label (plural)',
                  count: String(filteredCount),
                })
            : totalCount === 1
              ? gt('{filtered} of {total} attachment', {
                  context: 'filtered attachment count label (singular)',
                  filtered: String(filteredCount),
                  total: String(totalCount),
                })
              : gt('{filtered} of {total} attachments', {
                  context: 'filtered attachment count label (plural)',
                  filtered: String(filteredCount),
                  total: String(totalCount),
                });

        return (
          <div key={email.id} className="group">
            {/* Email item */}
            <div className="relative">
              {/* Email content */}
              <div className="pb-2">
                {/* Collapsed view */}
                {!isExpanded ? (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleEmailExpanded(email.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleEmailExpanded(email.id);
                      }
                    }}
                    className={`${emailCardClass(isHighlighted)} cursor-pointer hover:bg-muted/50`}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-medium text-sm truncate">
                        {email.subject ||
                          gt('No Subject', {
                            context: 'fallback when email has no subject',
                          })}
                      </span>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-muted-foreground">
                          {new Date(email.createdAt).toLocaleDateString(
                            [],
                            DATE_SHORT_FORMAT,
                          )}{' '}
                          {new Date(email.createdAt).toLocaleTimeString(
                            [],
                            TIME_FORMAT,
                          )}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleEmailExpanded(email.id);
                          }}
                        >
                          <CaretDownIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                      <span className="truncate">
                        <span className="font-medium">
                          <T context="email header field label">From:</T>
                        </span>{' '}
                        {email.from}
                      </span>
                      {totalCount > 0 && (
                        <>
                          <span>•</span>
                          <div className="flex items-center gap-1">
                            <PaperclipIcon size={10} />
                            <span>{attachmentsLabel}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Expanded view */
                  <div className={emailCardClass(isHighlighted)}>
                    <div className="space-y-2">
                      {/* Header */}
                      <div>
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-semibold text-sm flex-1">
                            {email.subject ||
                              gt('No Subject', {
                                context: 'fallback when email has no subject',
                              })}
                          </h4>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleEmailExpanded(email.id);
                            }}
                          >
                            <CaretUpIcon className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="flex items-center justify-between gap-2 mt-1">
                          <div className="text-xs text-muted-foreground space-y-0.5">
                            <div>
                              <span className="font-medium">
                                <T context="email header field label">From:</T>
                              </span>{' '}
                              {email.from}
                            </div>
                            <div>
                              <span className="font-medium">
                                <T context="email header field label">To:</T>
                              </span>{' '}
                              {email.to}
                            </div>
                            {email.cc && (
                              <div>
                                <span className="font-medium">
                                  <T context="email header field label">CC:</T>
                                </span>{' '}
                                {email.cc}
                              </div>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground shrink-0">
                            {new Date(email.createdAt).toLocaleString()}
                          </span>
                        </div>
                      </div>

                      {/* Body */}
                      <div className="pt-2 border-t">
                        <div className="prose prose-sm max-w-none">
                          {renderEmailBody(
                            email.htmlContent || email.textContent,
                            buildCidMap(email.documentAssociations),
                          )}
                        </div>
                      </div>

                      {/* Attachments */}
                      {filteredCount > 0 && (
                        <div className="pt-2 border-t">
                          <div className="flex items-center justify-between gap-2 text-xs font-medium text-muted-foreground mb-2">
                            <div className="flex items-center gap-1.5">
                              <PaperclipIcon size={12} />
                              <span>{attachmentsLabel}</span>
                            </div>
                            {sourceDocumentMatch && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-xs"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setShowAllAttachments((prev) => !prev);
                                }}
                              >
                                {showAll ? (
                                  <T context="button label to filter to just the invoice attachment">
                                    Show invoice attachment only
                                  </T>
                                ) : sourceIsSplitDocument ? (
                                  totalCount === 1 ? (
                                    gt('Show {count} original document', {
                                      context:
                                        'button label to show the original email attachments (singular)',
                                      count: String(totalCount),
                                    })
                                  ) : (
                                    gt('Show {count} original documents', {
                                      context:
                                        'button label to show the original email attachments (plural)',
                                      count: String(totalCount),
                                    })
                                  )
                                ) : (
                                  <T context="button label to show all email attachments">
                                    Show all attachments
                                  </T>
                                )}
                              </Button>
                            )}
                          </div>
                          {renderEmailAttachments(
                            filteredAttachments,
                            filteredDocumentAssociations,
                            highlightedDocumentIds,
                            handleRefreshData,
                          )}
                        </div>
                      )}

                      {/* Split-child documents (opt-in, fetched on demand).
                          The source split child already shows in the main
                          list above, so exclude it here. */}
                      <SplitDocumentsSection
                        count={
                          (email.splitDocumentCount ?? 0) -
                          sourceSplitAssociations.length
                        }
                        attachments={documentAssociationsToAttachmentItems(
                          email.splitDocumentAssociations?.filter(
                            (association) =>
                              association.document.id !== sourceDocumentId,
                          ),
                        )}
                        show={splitDocsExpandedEmails.has(email.id)}
                        onToggle={() => toggleSplitDocuments(email.id)}
                        isLoading={isFetching}
                        onRefreshData={handleRefreshData}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="h-full flex flex-col min-h-0 overflow-auto">
      <div className="space-y-2">
        {renderThreadHeader()}
        {renderThreadEmails()}
      </div>
    </div>
  );
}
